import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPurchaseOrderSyncableFields } from '@/services/apis/queries/purchase-order/use-get-purchase-order-syncable-fields';
import type { PurchaseOrderProductsSyncInformationsProps } from '../types/purchase-order-products-sync-informations.types';

type UsePurchaseOrderProductsSyncInformationsDialogParams = Pick<
  PurchaseOrderProductsSyncInformationsProps,
  'data'
>;

export function usePurchaseOrderProductsSyncInformationsDialog({
  data,
}: UsePurchaseOrderProductsSyncInformationsDialogParams) {
  const hasInitializedSelectionRef = useRef(false);
  const { t } = useTranslation('purchaseOrder');
  const {
    counts,
    dialog: { isOpen, isSubmitting, selectedFieldKeys, setSelectedFieldKeys },
  } = data;
  const {
    data: syncableFields = [],
    isPending,
    isError,
  } = useGetPurchaseOrderSyncableFields(isOpen);
  const { hasSelectedProducts, selectedProductsCount, totalProductsCount } =
    counts;

  const syncableFieldKeys = useMemo(
    () => syncableFields.map((field) => field.key),
    [syncableFields],
  );
  const sections = useMemo(
    () =>
      Array.from(
        syncableFields
          .reduce((groupedFields, field) => {
            groupedFields.set(field.section, [
              ...(groupedFields.get(field.section) ?? []),
              field,
            ]);
            return groupedFields;
          }, new Map<string, typeof syncableFields>())
          .entries(),
      ),
    [syncableFields],
  );
  const filteredSelectedFieldKeys = useMemo(
    () => selectedFieldKeys.filter((key) => syncableFieldKeys.includes(key)),
    [selectedFieldKeys, syncableFieldKeys],
  );

  useEffect(() => {
    if (!isOpen) {
      hasInitializedSelectionRef.current = false;
      return;
    }

    if (hasInitializedSelectionRef.current) {
      return;
    }

    if (!isOpen || isPending || isError || syncableFieldKeys.length === 0) {
      return;
    }

    if (
      selectedFieldKeys.length === filteredSelectedFieldKeys.length &&
      selectedFieldKeys.every(
        (key, index) => key === filteredSelectedFieldKeys[index],
      )
    ) {
      hasInitializedSelectionRef.current = true;
      return;
    }

    setSelectedFieldKeys(filteredSelectedFieldKeys);
    hasInitializedSelectionRef.current = true;
  }, [
    filteredSelectedFieldKeys,
    isError,
    isOpen,
    isPending,
    selectedFieldKeys,
    setSelectedFieldKeys,
    syncableFieldKeys,
  ]);

  const targetedProductsCount = hasSelectedProducts
    ? selectedProductsCount
    : totalProductsCount;
  const targetedProductsLabel = hasSelectedProducts
    ? t('syncInformationsSelectedProductsCount', {
        count: targetedProductsCount,
      })
    : t('syncInformationsTargetedProductsCount', {
        count: targetedProductsCount,
      });
  const isConfirmDisabled =
    filteredSelectedFieldKeys.length === 0 ||
    syncableFields.length === 0 ||
    isPending ||
    isError ||
    isSubmitting;

  const toggleField = useCallback(
    (fieldKey: string, checked: boolean | 'indeterminate') => {
      if (checked === 'indeterminate') {
        return;
      }

      setSelectedFieldKeys((currentFieldKeys) => {
        if (checked) {
          return currentFieldKeys.includes(fieldKey)
            ? currentFieldKeys
            : [...currentFieldKeys, fieldKey];
        }

        return currentFieldKeys.filter(
          (currentFieldKey) => currentFieldKey !== fieldKey,
        );
      });
    },
    [setSelectedFieldKeys],
  );

  return {
    isPending,
    isError,
    sections,
    targetedProductsLabel,
    filteredSelectedFieldKeys,
    isConfirmDisabled,
    toggleField,
  };
}
